import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Container, Row, Col, Button } from 'reactstrap'

import { InactivityOverlay, CheckoutAssistantComponents } from '@components'
import { useCartRedux, useCashRegister, useDashboardRedux, usePaymentEvents, usePubsubRedux } from '@hooks'
import { employeeServices, ErrorTrackingAPI } from '@services'
import { storageHelpers, logger } from '@utils'
import { customToast, findGroupByJoinedName, parseGroupId } from '@common'

const CheckoutAssistantPage = () => {
    const [isOpen, setIsOpen] = useState(false)
    // const [employee, setEmployee] = useState(null)
    const hasFetchedEmployee = useRef(false)

    const open = useCallback(() => setIsOpen(true), [])
    const close = useCallback(() => setIsOpen(false), [])

    const { assistantInfo, assistantCarts } = useCartRedux()
    const { joinedGroupName, isAutoJoining, joinGroupByName } = useCashRegister()
    const { selectedGroupId, cafeGroups, fetchCafeGroups } = useDashboardRedux()
    const { activeGroupId } = usePubsubRedux()
    const { paymentEvent, handlePaymentEventClose, handlePaymentRequest } = usePaymentEvents()

    // Fetch employee info and auto-connect to group
    useEffect(() => {
        const fetchEmployeeAndConnect = async () => {
            // Skip if already fetched successfully or already joined a group
            if (hasFetchedEmployee.current || joinedGroupName) return

            const tokens = employeeServices.getEmployeeTokens()
            if (!tokens.accessToken || !tokens.expiredAt || new Date(tokens.expiredAt) <= new Date()) {
                logger.warn('No valid employee token found')
                return
            }

            try {
                // Fetch employee info first, then set flag only on success
                const result = await employeeServices.retrieveEmployee(tokens.accessToken)
                if (result.success && result.employee) {
                    // Mark as fetched only after successful retrieval
                    hasFetchedEmployee.current = true

                    // Set employee context in Sentry
                    ErrorTrackingAPI.setEmployeeContext(result.employee)

                    // Build group name from employee info
                    const groupName = employeeServices.buildGroupNameFromEmployee(result.employee)
                    if (groupName) {
                        // Store group name for reconnection
                        storageHelpers.setCheckoutAssistantCashRegisterId(groupName)
                        logger.info(`Auto-connecting to group: ${groupName}`)

                        // Join the group
                        await joinGroupByName(groupName)
                    } else {
                        logger.warn('Cannot build group name from employee info', result.employee)
                        customToast.warning('Unable to connect to checkout assistant. Please contact support.', {
                            duration: 5000,
                        })
                    }
                } else {
                    // On failure, don't set flag to allow retry on next render
                    ErrorTrackingAPI.sendError(new Error(result.error), 'CheckoutAssistantPage', 'fetchEmployeeAndConnect')
                    logger.error('Error fetching employee and connecting:', result.error)
                    customToast.error('Failed to connect to checkout assistant. Please try again.', { duration: 5000 })
                }
            } catch (error) {
                // On error, don't set flag to allow retry on next render
                ErrorTrackingAPI.sendError(error, 'CheckoutAssistantPage', 'fetchEmployeeAndConnect')
                logger.error('Error fetching employee and connecting:', error)
            }
        }

        fetchEmployeeAndConnect()
    }, [joinedGroupName, joinGroupByName])

    const hasRequestedCafeGroups = useRef(false)
    useEffect(() => {
        // Ensure cafeGroups are loaded even if SettingsModal never opens (e.g., auto-join path)
        if (hasRequestedCafeGroups.current) return
        hasRequestedCafeGroups.current = true
        fetchCafeGroups()
    }, [fetchCafeGroups])

    const currentCart = joinedGroupName ? assistantCarts[joinedGroupName] : null
    const stallName = useMemo(() => {
        // Try to find group by joinedGroupName first
        if (joinedGroupName) {
            const match = findGroupByJoinedName(joinedGroupName, cafeGroups)
            if (match) {
                return match.name
            }
        }

        // Fallback: try to find by activeGroupId or selectedGroupId
        const candidates = [activeGroupId, selectedGroupId].filter(Boolean)

        const findName = id => {
            if (!id || !Array.isArray(cafeGroups) || cafeGroups.length === 0) return null

            const candidate = String(id)
            // Try to match with numeric ID or formatted ID
            const match = cafeGroups.find(group => {
                const gid = String(group.id)
                // Match direct ID or try with pos- prefix
                return gid === candidate
            })
            return match?.name
        }

        const name = candidates.map(findName).find(Boolean)
        return name || 'Local Chapter'
    }, [cafeGroups, joinedGroupName, activeGroupId, selectedGroupId])

    return (
        <div
            className="d-flex flex-column p-1 p-sm-2 p-md-3 position-relative"
            style={{ fontSize: '1.1rem', height: '100dvh', overflow: 'hidden' }}
        >
            <Container fluid className="d-flex flex-column" style={{ flex: 1, overflow: 'hidden' }}>
                <Row className="align-items-center mb-4 flex-shrink-0">
                    <Col className="d-flex justify-content-between align-items-center gap-5">
                        <div className="d-flex align-items-end justify-content-center gap-3">
                            <div className="display-6 m-0 fw-bold" style={{ fontSize: '2.5rem' }}>
                                Order Summary
                            </div>
                            <div className="display-6 m-0 fw-bold text-primary" style={{ fontSize: '2.5rem' }}>
                                {assistantInfo?.orderNumber ? `#${assistantInfo?.orderNumber}` : ''}
                            </div>
                        </div>

                        <CheckoutAssistantComponents.GroupConnectionStatus
                            isAutoJoining={isAutoJoining}
                            joinedGroupName={joinedGroupName}
                            cafeGroups={cafeGroups}
                        />
                    </Col>
                    <Col xs="auto">
                        <Button
                            color="light"
                            className="rounded-circle border border-3 border-dark d-inline-flex align-items-center justify-content-center p-0"
                            style={{
                                width: 56,
                                height: 56,
                                minWidth: 56,
                                minHeight: 56,
                                touchAction: 'manipulation',
                            }}
                            aria-label="Settings"
                            onClick={open}
                        >
                            <i className="bx bx-cog icon-close " style={{ fontSize: '1.5rem' }} />
                        </Button>
                    </Col>
                </Row>

                <main className="d-flex flex-column" style={{ flex: 1, overflow: 'hidden' }}>
                    {/* Cart display */}
                    <div className="rounded-4 bg-white d-flex flex-column" style={{ flex: 1, overflow: 'hidden' }}>
                        <CheckoutAssistantComponents.CartDisplay
                            currentCart={currentCart}
                            assistantInfo={assistantInfo}
                            isAutoJoining={isAutoJoining}
                        />
                    </div>
                </main>
            </Container>

            <CheckoutAssistantComponents.SettingsModal isOpen={isOpen} toggle={close} />

            <InactivityOverlay
                idleSeconds={60}
                message={`Welcome to ${stallName}`}
                isActive={!paymentEvent.isVisible && (!currentCart || !currentCart.items || currentCart.items.length === 0)}
            />

            <CheckoutAssistantComponents.PaymentEventModal
                isVisible={paymentEvent.isVisible}
                eventType={paymentEvent.eventType}
                eventData={paymentEvent.eventData}
                onClose={handlePaymentEventClose}
                onPaymentRequest={handlePaymentRequest}
            />
        </div>
    )
}

export default CheckoutAssistantPage
